import React, { useEffect, useState } from 'react';
import { Button, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useDispatch } from 'react-redux';
import { getOrderById, setVoidLabel } from '../../api/orders';
import { recheckShipmentIssue } from '../../api/shipments';
import { Notifications } from '../../store/slices';
import { AppDialog, EmptyState, StatusChip } from '../../components/ui';
import { formatAmount, formatDate } from '../../lib/formatDate';
import { useStyles } from './styles';

interface MultipleLabelPopupProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: number | null;
    clientId: number | null;
    issueId: number | null;
    onSuccess: (recheckResult?: any) => void;
}

/** Lists every label of the order so the operator can void the extra ones; the issue is re-checked after each void. */
const MultipleLabelPopup: React.FC<MultipleLabelPopupProps> = ({ isOpen, onClose, orderId, clientId, issueId, onSuccess }) => {
    const classes = useStyles();
    const [orderData, setOrderData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [voidingId, setVoidingId] = useState<number | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        if (isOpen && orderId && clientId) {
            fetchOrder();
        } else {
            setOrderData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderId, clientId]);

    const fetchOrder = async () => {
        setLoading(true);
        try {
            const data = await getOrderById({ id: orderId, companyId: clientId });
            setOrderData(data);
        } catch (error) {
            console.error('Failed to fetch order', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVoid = async (shipmentId: number) => {
        if (voidingId !== null) return;
        setVoidingId(shipmentId);
        try {
            await setVoidLabel({ shipmentId, onSuccess: () => {} });
            dispatch(Notifications.setNotifications({ text: 'Shipment voided', type: 'success' }));

            if (issueId) {
                try {
                    const recheckResponse = await recheckShipmentIssue({ issueId });
                    onSuccess(recheckResponse.result);
                } catch (recheckError) {
                    console.error('Recheck failed', recheckError);
                    onSuccess(); // fallback
                }
            } else {
                onSuccess();
            }
            onClose();
        } catch (error: any) {
            console.error('Failed to void shipment', error);
            dispatch(Notifications.setNotifications({ text: error.message || 'Failed to void shipment', type: 'error' }));
        } finally {
            setVoidingId(null);
        }
    };

    const shipments: any[] = orderData?.shipments ?? [];

    return (
        <AppDialog
            open={isOpen}
            onClose={onClose}
            title={`Resolve multiple labels for order #${orderId ?? ''}`}
            description="The order has more than one label. Void the extra ones; the remaining label stays active and the issue is re-checked."
            pending={voidingId !== null}
            maxWidth="lg"
            actions={<Button variant="outlined" onClick={onClose}>Close</Button>}
        >
            {loading ? (
                <div className={classes.skeletons} role="status" aria-label="Loading shipments">
                    {[0, 1].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
                </div>
            ) : shipments.length > 0 ? (
                <div className={classes.scroll}>
                    <Table aria-label="Labels of the order" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Shipment</TableCell>
                                <TableCell>Tracking number</TableCell>
                                <TableCell>Carrier</TableCell>
                                <TableCell>Service</TableCell>
                                <TableCell>Ship date</TableCell>
                                <TableCell align="right">Cost</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shipments.map((shipment: any) => (
                                <TableRow key={shipment.shipmentId}>
                                    <TableCell className={classes.mono}>{shipment.shipmentId}</TableCell>
                                    <TableCell className={classes.mono}>{shipment.trackingNumber || '—'}</TableCell>
                                    <TableCell>{shipment.carrierCode || '—'}</TableCell>
                                    <TableCell>{shipment.serviceCode || '—'}</TableCell>
                                    <TableCell className={classes.nowrap}>{formatDate(shipment.shipDate)}</TableCell>
                                    <TableCell className={classes.numeric}>{formatAmount(shipment.shipmentCost)}</TableCell>
                                    <TableCell>
                                        <StatusChip size="sm" dot tone={shipment.voided === 1 ? 'danger' : 'success'}>
                                            {shipment.voided === 1 ? 'Voided' : 'Active'}
                                        </StatusChip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            disabled={shipment.voided === 1 || voidingId !== null}
                                            onClick={() => handleVoid(shipment.shipmentId)}
                                        >
                                            {voidingId === shipment.shipmentId ? 'Voiding…' : 'Void'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <EmptyState title="No shipments on this order" hint="There is nothing to void here." />
            )}
        </AppDialog>
    );
};

export default MultipleLabelPopup;
